/**
 * Service de génération HTML pour les documents
 * Crée un fichier HTML autonome avec tous les styles intégrés
 */

export async function generateHTML(
  elementId: string,
  filename: string
): Promise<void> {
  try {
    // Trouver l'élément
    const element = document.getElementById(elementId);
    
    if (!element) {
      throw new Error(`Élément avec l'ID "${elementId}" introuvable`);
    }

    // Cloner l'élément pour ne pas modifier l'original
    const clonedElement = element.cloneNode(true) as HTMLElement;

    // Fonction récursive pour appliquer les styles calculés
    const applyComputedStyles = (original: HTMLElement, clone: HTMLElement) => {
      const computed = window.getComputedStyle(original);
      const cloneStyle = clone.style;
      
      // Copier les propriétés CSS importantes
      const importantProps = [
        'color', 'backgroundColor', 'fontSize', 'fontFamily', 'fontWeight',
        'padding', 'margin', 'border', 'borderRadius', 'display', 'flexDirection',
        'justifyContent', 'alignItems', 'gap', 'width', 'height', 'maxWidth',
        'minWidth', 'textAlign', 'lineHeight', 'boxShadow', 'background',
        'backgroundImage', 'backgroundGradient', 'opacity', 'transform'
      ];
      
      importantProps.forEach(prop => {
        const value = computed.getPropertyValue(prop);
        if (value) {
          cloneStyle.setProperty(prop, value);
        }
      });
      
      // Copier toutes les propriétés personnalisées (variables CSS)
      Array.from(computed).forEach(prop => {
        if (prop.startsWith('--')) {
          cloneStyle.setProperty(prop, computed.getPropertyValue(prop));
        }
      });
      
      // Traiter les enfants
      const originalChildren = Array.from(original.children) as HTMLElement[];
      const cloneChildren = Array.from(clone.children) as HTMLElement[];
      
      originalChildren.forEach((origChild, index) => {
        if (cloneChildren[index]) {
          applyComputedStyles(origChild, cloneChildren[index]);
        }
      });
    };
    
    // Appliquer les styles calculés au clone
    applyComputedStyles(element, clonedElement);
    
    // Cacher les boutons dans le clone
    const buttons = clonedElement.querySelectorAll('button');
    buttons.forEach(btn => {
      (btn as HTMLElement).style.display = 'none';
    });
    
    // Récupérer tous les styles CSS de la page
    const allStyles: string[] = [];
    
    // Récupérer les styles des feuilles de style
    Array.from(document.styleSheets).forEach((styleSheet) => {
      try {
        if (styleSheet.cssRules) {
          Array.from(styleSheet.cssRules).forEach((rule) => {
            if (rule instanceof CSSStyleRule || rule instanceof CSSMediaRule) {
              allStyles.push(rule.cssText);
            }
          });
        }
      } catch (e) {
        // Ignorer les erreurs CORS pour les feuilles de style externes
        console.warn('Impossible de lire les styles:', e);
      }
    });

    // Récupérer les styles inline
    const inlineStyles = Array.from(document.querySelectorAll('style')).map(
      (style) => style.innerHTML
    );

    // Créer le HTML complet
    const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${filename.replace('.html', '')}</title>
  <style>
    /* Reset et base */
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #1f2937;
      background-color: #f9fafb;
      padding: 2rem;
    }
    
    /* Styles généraux */
    ${allStyles.join('\n')}
    
    ${inlineStyles.join('\n')}
    
    /* Styles spécifiques pour l'export HTML */
    .html-export-container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
      padding: 2rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }
    
    /* Assurer que les images sont chargées */
    img {
      max-width: 100%;
      height: auto;
    }
    
    /* Améliorer l'impression */
    @media print {
      body {
        padding: 0;
        background: white;
      }
      
      .html-export-container {
        box-shadow: none;
        padding: 1rem;
      }
      
      @page {
        margin: 1cm;
      }
    }
    
    /* Cacher les boutons */
    button {
      display: none !important;
    }
  </style>
</head>
<body>
  <div class="html-export-container">
    ${clonedElement.outerHTML}
  </div>
  
  <!-- Footer avec icône Sparkles -->
  <div style="text-align: center; margin-top: 3rem; padding-top: 2rem; border-top: 2px solid #e5e7eb; color: #6b7280; font-size: 0.875rem;">
    <div style="margin-bottom: 1rem; display: flex; justify-content: center; align-items: center;">
      <div style="background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%); padding: 12px; border-radius: 12px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: block;">
          <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
          <path d="M5 3v4"/>
          <path d="M19 17v4"/>
          <path d="M3 5h4"/>
          <path d="M17 19h4"/>
          <path d="M7 21l-2-2"/>
          <path d="M17 3l2 2"/>
        </svg>
      </div>
    </div>
    <p style="font-weight: 600; color: #374151; margin-bottom: 0.5rem;">Généré par PRIsME</p>
    <p style="margin-bottom: 0.25rem;">Excellence TI propulsée par l'IA, enrichie et validée par l'expertise humaine</p>
    <p style="font-size: 0.75rem; color: #9ca3af;">© ${new Date().getFullYear()} BZ inc. Tous droits réservés.</p>
  </div>
</body>
</html>`;

    // Créer un blob et télécharger
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.html') ? filename : `${filename}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('HTML généré et téléchargé:', filename);
  } catch (error) {
    console.error('Erreur lors de la génération du HTML:', error);
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
    alert(`Une erreur est survenue lors de la génération du HTML:\n${errorMessage}\n\nVeuillez réessayer.`);
  }
}
